<script>
    import _ from 'lodash'
    import moment from 'moment'
    import $ from '../shared/helper'
    import Cleave from 'cleave.js'
    import {
        CONST,
        FIELD_PROPERTIES,
        HOVER_BUTTONS_GLOBAL,
        HOVER_BUTTONS,
        DEFAULT_FLAGS,
        DEFAULT_DATE,
        REGEX_IS_TEXT
    } from '../shared/constants'
    import {ATYPES} from '../state/types'
    import {gfComputed} from '../state/map'
    import VDateRange from './v-date-range.vue'
    import VGridformSlider from './v-gridform-slider'
    import VRatingButton from '@src/plugins/vue-gridform/components/v-rating-button'
    import * as Moment from 'moment'
    import PasswordInput from '@components/password-input.vue'

    const gfFieldHelper = {
        sanitizeMultiple(fldDef, field) {
            fldDef.items = field.items
            if (fldDef.multiple) {
                fldDef.value = Array.isArray(field.value) ? field.value : field.value ? [field.value] : field.value
                fldDef.clearable = true
            }
            else {
                // if we have a value, check if value is valid (exists in current items)
                // if radiogroup switched from multiple to not multiple we have still an array as value
                if (!Array.isArray(field.value)) {
                    if (_.find(field.items, (item) => item.value === field.value)) fldDef.value = field.value
                }
                else fldDef.value = ''

                if (!fldDef.value) {
                    const firstItem = _.first(fldDef.items)
                    // check if we need only the value or the selected item object
                    // string[] or object[]
                    if (_.isString(firstItem)) fldDef.value = field.required ? firstItem : ''
                    else {
                        const selected = _.find(fldDef.items, item => item.selected)
                        if (selected) fldDef.value = selected
                        else fldDef.value = field.required ? firstItem : ''
                    }
                }
                // fldDef.value = fldDef.value || (_.find(field.items, (item) => item.selected) || {}).value
                // if (!fldDef.value && !_.isEmpty(field.items)) fldDef.value = _.first(field.items).value
                // TODO why no clearable in single selection?
                fldDef.clearable = !field.required
            }
        },
        styleSelections(fldDef, field) {
            fldDef.multiple = $.bool(field.multiple, true)
            fldDef.chips = $.bool(field.chips, true)
            fldDef.smallChips = $.bool(field.smallChips, fldDef.chips)
            fldDef.deletableChips = $.bool(field.deletableChips, fldDef.chips)
            fldDef.menuProps = {maxHeight: 400, dense: true}
            // fldDef.menuProps = {light: !global.dark}
        }
    }

    let _LAST_INPUT_NAME = ''

    export default {
        name: 'VGridformField',
        components: {
            PasswordInput,
            VRatingButton,
            VGridformSlider,
            VDateRange
        },
        // [name="gfuuid", field={props}, rowinfo=[]]
        props: {
            name: {
                type: String,
                required: true
            },
            // field configuration
            field: {
                type: Object,
                required: false,
                // TODO get config from gridforms.js
                default: () => FIELD_PROPERTIES
            },
            rowinfo: {
                type: Array,
                default: () => null
            }
        },
        data() {
            return {
                loading: false,
                currentData: null,
                now: new Date().toISOString(),
                dialog: null,
                date: '',
                time: '',
                hoverCanceled: false,
                search: null
            }
        },

        computed: {
            ...gfComputed,

            wrapperClasses() {
                const bpclasses = this.field.type === 'spacer' ? '' : `xs12 sm12 md${this.field.size || 12}`

                let gaps = 'py-0'
                if (this.config.editable) gaps = 'py-0 ' // no padding top in edit mode to prevent gap before toolbar
                // if (this.field.type === 'slider') gaps = 'pb-4' // more space for slider ticks

                let classes = `${gaps} `

                // TODO fix grow / shrink
                if (this.field.grow) classes += 'flex-grow-1 '
                // if (this.field.shrink) classes += 'flex-shrink-0 '

                if (this.field.center) classes += 'align-self-center '
                if (this.field.flat) classes += 'flat '
                if (this.field.hidden) classes += 'd-none '

                return `${bpclasses} ${classes}`.trim()
            },

            fieldId() {
                return this.field.name ? `${CONST.CLASS_FIELD}-${this.field.name}` : null
            },
            fieldClasses() {
                const type = this.field.type
                let cls = this.field.padClass ? `${this.field.padClass} ` : 'pa-2 '
                if (type === 'divider') cls = ''
                if (type === 'subheader') cls += 'headline'
                return `${CONST.CLASS_FIELD}-${type} ${cls}`.trim()
            },
            // create field definition object for component rendering (v-bind)
            // this = VGridformField instance
            // NOTE does not change anything on vuex store states
            fieldDef() {
                const { field, global, permissions, fieldClasses, config } = this
                const lang = this.$vuetify.lang.current

                const fldDef = {
                    _type: field.type, // sync field type to fieldDef, do not use "type", it is a valid vuetify prop
                    name: field.name,
                    value: field.value,
                    label: field.label,
                    [global.icon]: field.icon,
                    disabled: $.bool(field.disabled, DEFAULT_FLAGS.disabled),
                    readonly: $.bool(field.readonly, DEFAULT_FLAGS.readonly),
                    required: $.bool(field.required, DEFAULT_FLAGS.required),
                    spellcheck: $.bool(field.spellcheck, DEFAULT_FLAGS.spellcheck),
                    clearable: $.bool(field.clearable, DEFAULT_FLAGS.clearable),
                    autocomplete: field.browserAutocomplete || DEFAULT_FLAGS.browserAutocomplete,
                    hideDetails: true,
                    // TODO disable vuetify rules??
                    // rules: field.rules,
                    validateOnBlur: $.bool(field.validateOnBlur, DEFAULT_FLAGS.validateOnBlur),
                    class: (fieldClasses + ' ' + (field.cls || '')).trim(),
                    dense: $.bool(field.dense, DEFAULT_FLAGS.dense),
                    rating: !!config.rating && config.editable && field.rating !== false,
                    weighting: field.weighting,
                    // auto tabindex for fields and action buttons
                    tabindex: field.tabindex || '0'
                }

                if (field.appendIcon) fldDef.appendIcon = field.appendIcon
                if (field.prependIcon) fldDef.prependIcon = field.prependIcon
                if (field.prependInnerIcon) fldDef.prependInnerIcon = field.prependInnerIcon

                if (this.isText) {
                    _.extend(fldDef, {
                        filled: field.fieldstyle === 'filled',
                        outlined: field.fieldstyle === 'outlined',
                        solo: field.fieldstyle === 'solo'
                    })
                    if (fldDef.solo && field.flat) fldDef.flat = true
                    if (field.rounded) fldDef.rounded = true
                    if (field.shaped && (fldDef.outlined || fldDef.filled)) fldDef.shaped = true
                    if (field.maxlength) fldDef.maxlength = field.maxlength
                    if (field.maxlength && field.counter !== false) fldDef.counter = true
                    if (field.inputType) fldDef.type = field.inputType
                    if (field.autofocus) fldDef.autofocus = field.autofocus
                    if (field.prefix) fldDef.prefix = field.prefix
                    if (field.suffix) fldDef.suffix = field.suffix
                }

                if (field.type === 'password') {
                    fldDef.type = 'password'
                    fldDef[global.icon] = fldDef[global.icon] || 'mdi-lock'
                    // fldDef.autocomplete = 'off'
                    // fldDef.pattern = '[a-z][A-Z][0-9][^!/{([)]=}?*~\'#-_.:,;<>|@"\\ ]'
                }
                else if (field.type === 'textarea') {
                    if (field.rows) fldDef.rows = field.rows
                    if (field.readonly) {
                        // field.disabled = true
                        // field.soloInverted = true
                    }
                    fldDef.autoGrow = true
                    fldDef.height = 'auto'
                    fldDef.noResize = true
                }
                else if (field.type === 'number') {
                    fldDef.type = 'number'
                    fldDef.step = field.step
                    if (!_.isUndefined(field.min) && !_.isUndefined(field.max)) {
                        fldDef.min = field.min
                        fldDef.max = field.max
                        if (field.hint !== false) fldDef.hint = field.hint || `Wertebereich ${fldDef.min}-${fldDef.max}`
                    }
                    fldDef.clearable = false
                    if (_.isNaN(fldDef.value)) fldDef.value = fldDef.min || 0
                    fldDef.pattern = '[0-9]*'
                    fldDef.inputmode = 'numeric'
                }
                else if (field.type === 'autocomplete') {
                    fldDef.hideNoData = true
                    fldDef.returnObject = true
                    gfFieldHelper.styleSelections(fldDef, field)
                }
                else if (field.type === 'date') {
                    fldDef.readonly = false
                    fldDef[global.icon] = fldDef[global.icon] || DEFAULT_DATE.icon
                    const {datePlaceholder, dateFormat} = this.config.formatter
                    fldDef.placeholder = field.placeholder || datePlaceholder || DEFAULT_DATE[`hint_${lang}`]
                    fldDef.format = fldDef.format || dateFormat || DEFAULT_DATE.format
                    if (fldDef.value) {
                        if (/^(now)$/i.test(fldDef.value)) {
                            fldDef.value = moment().format(fldDef.format)
                        }
                        const mom = moment(fldDef.value, fldDef.format)
                        if (mom.isValid()) fldDef.hint = mom.format('dddd, MMMM')
                    }
                    fldDef.locale = lang === 'de' ? 'de-de' : 'en-us'
                }
                else if (field.type === 'daterange') {
                    fldDef.readonly = false
                    fldDef[global.icon] = fldDef[global.icon] || DEFAULT_DATE.icon
                    fldDef.hint = fldDef.hint || DEFAULT_DATE.hint
                    fldDef.format = fldDef.format || DEFAULT_DATE.format
                    fldDef.time = field.time
                    fldDef.locale = this.$vuetify.lang.current === 'de' ? 'de-de' : 'en-us'
                    if (field.value) fldDef.value = _.isArray(field.value) ? field.value : [field.value]
                }
                else if (field.type === 'time') {
                    fldDef.format = '24hr'
                    fldDef[global.icon] = fldDef[global.icon] || 'mdi-clock-outline'
                    // fldDef.hint = fldDef.hint || 'HH:MM'
                    fldDef.placeholder = field.placeholder || 'hh:mm'
                    // fldDef.pattern = '[0-9]*'
                    fldDef.inputmode = 'numeric'
                }
                else if (field.type === 'combobox') {
                    fldDef.items = field.items
                    fldDef.itemValue = 'value'
                    fldDef.itemText = 'label'
                    gfFieldHelper.styleSelections(fldDef, field)
                    // gfFieldHelper.sanitizeMultiple(fldDef, field)
                    // console.log('fldDef', fldDef, field)
                }
                else if (field.type === 'radio') {
                    fldDef.row = field.row !== false
                    fldDef.items = field.items
                    fldDef.multiple = $.bool(field.multiple, false)
                    fldDef.mandatory = field.required
                    if (fldDef.multiple) {
                        fldDef.value = Array.isArray(field.value) ? field.value : field.value ? [field.value] : field.value
                        fldDef.value = fldDef.value || []
                        fldDef.clearable = true
                    }
                    else {
                        // if we have a value, check if value is valid (exists in current items)
                        // if radiogroup switched from multiple to not multiple we have still an array as value
                        if (!Array.isArray(field.value)) {
                            if (_.find(field.items, (item) => item.value === field.value)) fldDef.value = field.value
                        }
                        else fldDef.value = ''

                        if (!fldDef.value) {
                            const firstItem = _.first(fldDef.items)
                            // check if we need only the value or the selected item object
                            // string[] or object[]
                            if (_.isString(firstItem)) fldDef.value = field.required ? firstItem : ''
                            else {
                                const selected = _.find(fldDef.items, item => item.selected)
                                if (selected) fldDef.value = selected
                                else fldDef.value = field.required ? firstItem : ''
                            }
                        }
                        fldDef.clearable = false
                    }
                }
                else if (field.type === 'select') {
                    gfFieldHelper.styleSelections(fldDef, field)
                    fldDef.items = field.items
                    if (fldDef.multiple && !_.isArray(fldDef.value)) {
                        const current = _.find(fldDef.items, {value: fldDef.value})
                        fldDef.value = _.isEmpty(current) ? [] : [current]
                    }
                    if (field.disabledEmpty && _.isEmpty(fldDef.value)) fldDef.disabled = true
                }
                else if (field.type === 'checkbox' || field.type === 'switch') {
                    fldDef.trueValue = true
                    fldDef.falseValue = false
                    fldDef.inputValue = $.bool(field.value, false)
                    fldDef.clearable = false
                    fldDef.validateOnBlur = false // must be validated immediatly, no blur fired
                }
                // :min="4" :max="12" :range="1" hide-details :tick-labels="[4,5,6,7,8,9,10,11,12]
                else if (field.type === 'slider' || field.type === 'range') {
                    if (field.type === 'range' && !_.isArray(fldDef.value)) fldDef.value = []
                    fldDef.min = field.min
                    fldDef.max = field.max
                    fldDef.step = field.step
                    fldDef.tickSize = 4 // default = 2
                    // use custom labels if exists
                    // const tickLabels = field.customLabels || field.tickLabels
                    // fldDef.tickLabels = tickLabels
                    // if (_.isString(tickLabels)) fldDef.tickLabels = tickLabels.split(',')
                    // if (!fldDef.tickLabels) fldDef.tickLabels = _.range(field.min, field.max + 1, field.step)
                    const tickLabels = field.customLabels
                    if (_.isString(tickLabels)) fldDef.tickLabels = tickLabels.split(',')
                    fldDef.tickLabels = _.range(field.min, field.max + 1, field.step)
                    // convert to string, otherwise 0 is missing
                    fldDef.tickLabels = fldDef.tickLabels.map(v => v.toString())
                    if (field.thumbLabel) {
                        fldDef.thumbLabel = field.thumbLabel
                        if (field.thumbSize) fldDef.thumbSize = field.thumbSize
                    }
                    if (field.type === 'slider') {
                        if (fldDef.value < fldDef.min) fldDef.value = fldDef.min
                        if (fldDef.value > fldDef.max) fldDef.value = fldDef.max
                        field.value = fldDef.value
                    }
                    // fix invalid range value
                    if (field.type === 'range') {
                        if (fldDef.value[0] > fldDef.max) fldDef.value[0] = fldDef.min
                        if (fldDef.value[1] > fldDef.max) fldDef.value[1] = fldDef.max
                        field.value = fldDef.value
                    }
                    fldDef.thumbSize = 24
                    if (fldDef.tickLabels.length > 50) {
                        fldDef.tickLabels = []
                        fldDef.thumbLabel = 'always'
                    }
                    // fldDef.ticks = true
                    // field.showInput = true
                }
                else if (field.type === 'phone') {
                    fldDef[global.icon] = fldDef[global.icon] || 'mdi-phone'
                    fldDef.hint = fldDef.hint || '(000) 000 - 0000'
                }
                else if (field.type === 'email') {
                    fldDef[global.icon] = fldDef[global.icon] || 'mdi-email-outline'
                    fldDef.hint = fldDef.hint || 'yourname@domain.suffix'
                }
                else if (field.type === 'button') {
                    // fldDef.flat = true
                    // fldDef.raised = true
                    // fldDef.depressed = true
                    fldDef.color = 'primary'
                    fldDef.appendIcon = field.appendIcon
                    fldDef.text = true
                    fldDef.block = field.block
                    if (field.href) {
                        fldDef.href = field.href
                        fldDef.target = field.target || '_blank'
                    }
                    // fldDef.class = `mt-4 ${fldDef.class}`.trim()
                }
                else if (/^(subheader|divider|spacer|gap)$/.test(field.type)) {
                    fldDef.rating = false
                    if (field.dense) fldDef.class = fldDef.class + ' v-subheader--dense'
                }
                else if (field.type === 'duration') {
                    fldDef[global.icon] = fldDef[global.icon] || 'mdi-timer'
                    fldDef.placeholder = 'hh:mm:ss'
                }
                else if (field.type === 'link') {
                    fldDef[global.icon] = fldDef[global.icon] || 'mdi-link'
                    fldDef.appendIcon = field.appendIcon || 'mdi-open-in-new'
                }
                else if (field.type === 'ratingbutton') {
                    fldDef.large = true
                    fldDef.size = 56
                    fldDef.options = {rating: field.options}
                }

                if ($.bool(field.speech, DEFAULT_FLAGS.speech) && permissions.microphone) {
                    fldDef.appendOuterIcon = permissions.microphone.allowed
                        ? 'mdi-microphone'
                        : permissions.microphone.error === 'network'
                            ? 'mdi-microphone-off'
                            : null
                }

                // Required text indicator
                // https://material.io/design/components/text-fields.html#anatomy
                // TODO use required OR optional hint, depending on number of fields in each category
                if (field.required) {
                    if (!/\*$/.test(field.label)) {
                        fldDef.label = fldDef.label + '*'
                    }
                    // create required rule for field def
                    // this way we fetch errors on field input blur
                    fldDef.rules = fldDef.rules || []
                    fldDef.rules.push(
                        v => !!v || this.$vuetify.lang.t('$vuetify.gf.required')
                    )
                }

                if (field.errors) fldDef.errorMessages = field.errors
                if (field.messages) fldDef.messages = field.messages

                let forceHint = false
                if (field.hint === 'error') {
                    if (!_.isEmpty(fldDef.errorMessages)) forceHint = true
                }
                else {
                    if (field.hint) fldDef.hint = field.hint
                    if (field.hint !== false) {
                        if (fldDef.hint || !_.isEmpty(field.rules) || fldDef.counter) forceHint = true
                    }
                }
                if (forceHint) {
                    fldDef.persistentHint = true
                    fldDef.hideDetails = false
                }

                if (field.forceDisabled) fldDef.disabled = true
                if (field.forceEnabled) fldDef.disabled = false
                if (fldDef.disabled) fldDef.clearable = false
                if (field.editMode) fldDef.editMode = true
                if (field.uppercase && fldDef.value) fldDef.value = fldDef.value.toUpperCase()
                // TODO check this -> only type "slider", rating is already set to true, without slider has no rating button
                if (field.rating && !_.isUndefined(field.rating.max)) {
                    fldDef.rating = field.rating
                    if (field.ratingScroll) fldDef.ratingScroll = true
                }
                if (field.export) fldDef.export = true

                // console.log('field', field)
                // console.log('fldDef', fldDef.name, fldDef)
                // console.log('render field', field.name, field.value)
                return fldDef
            },

            isText() {
                const { type = 'text' } = this.field
                return REGEX_IS_TEXT.test(type)
            },

            datePickerDef() {
                const dtefmt = 'YYYY-MM-DD'
                // const dtefmt = this.fieldDef.format
                return {
                    firstDayOfWeek: 1,
                    showWeek: true,
                    // default: Thu, Apr 1
                    titleDateFormat(d) {
                        return moment(d, dtefmt).format('dd, MMM D')
                    },
                    // default: M T M T F S S
                    weekdayFormat(d) {
                        return moment(d, dtefmt).format('dd')
                    },
                    // default: JAN FEB MAR APR
                    monthFormat(d) {
                        return moment(d, dtefmt).format('MMMM')
                    },
                    // default: April 1971
                    headerDateFormat(d) {
                        return moment(d, dtefmt).format('MMMM YYYY')
                    }
                }
            },
            timePickerDef() {
                return {
                    format: '24hr'
                }
            },

            config() {
                return this.$store.getters[$.ns('config', this.name)] || {}
            },
            fieldData() {
                return this.$store.getters[`${CONST.NAMESPACE}/_${this.name}/fieldData`](this.field.name)
            },
            acData() {
                return this.$store.getters[`${CONST.NAMESPACE}/${this.field.name}`]
            },

            hoverButtons() {
                return HOVER_BUTTONS[this.field.type]
            },
            hoverButtonsGlobal() {
                return HOVER_BUTTONS_GLOBAL
            }
        },
        watch: {
            // textfield in datepicker dialog triggers no @change event
            date(value) {
                value = value || '' // clear in date input returns "null" and this trigger wrong payload
                this.currentData = value
                const ndata = {[this.field.name]: {value}}
                this.dispatch(ATYPES.GFCHANGE, ndata)
            },
            // value must be masked, otherwise NaN:NaN is displayed
            time(value) {
                value = value || '' // clear in date input returns "null" and this trigger wrong payload
                this.currentData = value
                const ndata = {[this.field.name]: {value}}
                this.dispatch(ATYPES.GFCHANGE, ndata)
            }
        },

        created() {
            if (!_.includes(CONST.VALID_FIELDS, this.field.type)) {
                console.debug('vue-gridform: field type "%s" currently not supported!', this.field.type)
            }

            let mom
            if (this.field.type === 'date') {
                if (this.field.value) {
                    mom = moment(this.field.value, Moment.ISO_8601)
                    if (mom.isValid()) this.date = mom.format('YYYY-MM-DD')
                }
                else this.date = ''
            }
            else if (this.field.type === 'time') {
                if (this.field.value) {
                    mom = moment(this.field.value, Moment.ISO_8601)
                    if (mom.isValid()) this.time = mom.format('HH:mm')
                }
                else this.time = ''
            }
            else if (this.field.type === 'combobox') {
                if (_.isString(this.field.value)) this.search = this.field.value
            }
        },
        mounted() {
            if (this.config.mousetrap) {
                // TODO add other focusable controls
                const input = this.$el.querySelector('input')
                if (input) input.classList.add('mousetrap')
            }

            if (this.field.type === 'date') {
                const input = this.$el.querySelector('input')
                if (input) {
                    const {datePattern, delimiter} = this.config.formatter
                    new Cleave(input, {date: true, datePattern, delimiter})
                }
            }
            else if (this.field.type === 'time') {
                const input = this.$el.querySelector('input')
                if (input) {
                    new Cleave(input, {
                        time: true,
                        timePattern: ['h', 'm']
                    })
                }
            }
            else if (this.field.type === 'duration') {
                const input = this.$el.querySelector('input')
                if (input) {
                    new Cleave(input, {
                        time: true,
                        timePattern: ['h', 'm', 's']
                    })
                }
            }
        },
        updated() {
            if (this.field.type === 'date') {
                if (this.field.value !== this.date) this.date = this.field.value || ''
            }
            else if (this.field.type === 'time') {
                if (this.field.value !== this.time) this.time = this.field.value
            }
            // TODO check if combobox supports new entries??
            else if (this.field.type === 'combobox') {
                // console.log('this.field.value', this.field.value)
                // console.log('this.search', this.search)
                // TODO only initial? this.search is set on typing
                // NOTE this.field.value is an array on multiple select
                if (this.field.value && _.isString(this.field.value)) this.search = this.field.value
                // if (this.fieldData.def.editable && _.isString(this.field.value)) this.search = this.field.value
            }
        },

        methods: {
            /**
             * Dispatch Wrapper - check if gridform module is registered
             * @param {String} action - unprefixed action name
             * @param payload - action payload
             */
            dispatch(action, payload) {
                if (!action) return

                this.$store.dispatch(`${CONST.NAMESPACE}/_${this.name}/${action}`, payload)
                    .catch((reason) => {
                        console.error('dispatch error', reason)
                    })
            },

            foldStateIcon(collapsed) {
                return collapsed ? CONST.EXPAND_ICON : CONST.COLLAPSE_ICON
            },

            // triggered by autofill (Chrome)
            onChange(value) {
                // let items
                // TODO create utils method to fix value depending on field type
                if (this.field.type === 'number') value = parseInt(value, 10)
                else if (this.field.type === 'combobox') {
                    if (_.isArray(value)) {
                        value = value.map(v => {
                            if (_.isString(v)) return {value: v, label: v, avatar: '1'}
                            else return v
                        })
                    }
                }
                else if (this.field.type === 'radio') {
                    // console.log('this.field.multiple, value', this.field.multiple, value)
                }
                // TODO autocomplete send full data object as value
                // TODO initialize value depending on field type? to prevent undefined as value
                this.currentData = value
                const ndata = {[this.field.name]: {value}}
                // if (items) ndata[this.field.name].items = items
                this.dispatch(ATYPES.GFCHANGE, ndata)
            },

            // triggered by keypress
            // triggered by form.reset() -> NOTE do not debounce
            // triggered by autofill (Firefox)
            onInput(value) {
                // TODO create utils method to fix value depending on field type
                if (this.isText) value = value || ''
                if (this.field.type === 'number') value = parseInt(value, 10)
                this.currentData = value
                // console.log('field "%s" onInput =', this.field.name, this.currentData, typeof value)
                const ndata = {[this.field.name]: {value}}
                // NOTE using ATYPES.GFUPDATE we have a problem with browser autofill (Firefox)
                //  because there will be 2 changes at the same time (name, password)
                //  but only the last will be updated when debouncing
                //  => only debounce when the same field as the last input is changed
                if (this.isText && _LAST_INPUT_NAME === this.field.name) this.dispatch(ATYPES.GFUPDATE, ndata)
                else this.dispatch(ATYPES.GFCHANGE, ndata)
                _LAST_INPUT_NAME = this.field.name
            },

            onFieldClick() {
                const field = _.cloneDeep(this.field)
                this.$emit('click', field)
            },
            onClick() {
                const field = _.cloneDeep(this.field)
                this.$emit('click', field)
            },
            onClickChip(item) {
                const field = _.cloneDeep(this.field)
                this.$emit('click:chip', field, item)
            },
            onCloseChip(item) {
                const value = Array.isArray(this.field.value) ? this.field.value.filter((v) => v !== item) : null
                const ndata = {[this.field.name]: {value}}
                this.dispatch(_.isUndefined(value) ? ATYPES.GFCHANGE : ATYPES.GFUPDATE, ndata)
            },
            onToggle() {
                this.dispatch(ATYPES.GFCHANGE, {[this.field.name]: {collapsed: !this.field.collapsed}})
            },

            onHover() {
                if (!this.config.hover || this.field.hovered || this.hoverCanceled) return

                const payload = { [this.field.name]: { hovered: true } }
                this.dispatch(ATYPES.GFCHANGE, payload)
                this.$emit('hover', payload)
            },
            onHoverOut() {
                if (!this.config.hover) return

                // TODO get set computed bauen?
                const payload = {[this.field.name]: {hovered: false}}
                this.dispatch(ATYPES.GFCHANGE, payload)
                this.$emit('hover', payload)
                _.delay(() => {
                    this.hoverCanceled = false
                }, 800)
            },
            onHoverChange(event) {
                const field = _.cloneDeep(this.field)
                const action = event.target.innerText
                const payload = {field, action}

                this.$emit('hoverchange', payload)

                // we use the cancel action to close the hover overlay
                if (action === 'cancel') {
                    this.hoverCanceled = true
                    this.onHoverOut()
                }
            },

            onPrependInnerIcon() {
                const field = _.cloneDeep(this.field)
                this.$emit('click:prepend-inner', field)
            },
            onAppendIcon() {
                // console.log('onAppendIcon', this)
                // this.$gridform.speech.start()
                const field = _.cloneDeep(this.field)
                this.$emit('click:append', field)
            },
            onSubmit() {
                this.$emit('submit')
            },

            onDateUp() {
                const format = this.fieldDef.format
                if (!this.date) this.date = moment().format(format)
                else this.date = moment(this.date).add(1, 'd').format(format)
            },
            onDateDown() {
                const format = this.fieldDef.format
                if (!this.date) this.date = moment().format(format)
                else this.date = moment(this.date).subtract(1, 'd').format(format)
            },
            onTimeUp() {
                if (!this.time) this.time = '00:00'
                else {
                    const time = this.time.split(':')
                    const hour = parseInt(time[0], 10)
                    const minute = parseInt(time[1], 10)
                    this.time = moment().set({hour, minute}).add(1, 'h').format('HH:mm')
                }
            },
            onTimeDown() {
                if (!this.time) this.time = '00:00'
                else {
                    const time = this.time.split(':')
                    const hour = parseInt(time[0], 10)
                    const minute = parseInt(time[1], 10)
                    this.time = moment().set({hour, minute}).subtract(1, 'h').format('HH:mm')
                }
            }
        }
    }
</script>

<template>
    <VFlex :id="fieldId" class="vgf-f__wrapper" :class="wrapperClasses" @click="onFieldClick">
        <slot name="prepend" :field="fieldDef" :cfg="config" :rowinfo="rowinfo" :data="fieldData"/>

        <VTextarea v-if="field.type === 'textarea'" v-bind="fieldDef" @input="onInput" @change="onChange" @click:append="onAppendIcon"/>
        <VAutocomplete v-else-if="field.type === 'autocomplete'" :items="acData" v-bind="fieldDef" :loading="loading" @change="onChange"/>
        <template v-else-if="field.type === 'date' && fieldDef.asMenu">
            <VMenu
                ref="dialog"
                v-model="dialog"
                :nudge-right="40"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                max-width="290"
            >
                <template v-slot:activator>
                    <VTextField v-model="date" v-bind="fieldDef" @click:prepend-inner.left="dialog = !dialog"/>
                </template>
                <VDatePicker v-model="date" v-bind="datePickerDef" scrollable>
                    <VSpacer/>
                    <VBtn text @click.left="dialog = false">{{ $vuetify.lang.t('$vuetify.actions.cancel') }}</VBtn>
                    <VBtn text @click.left="$refs.dialog.save(date)">{{ $vuetify.lang.t('$vuetify.actions.ok') }}</VBtn>
                </VDatePicker>
            </VMenu>
        </template>
        <template v-else-if="field.type === 'date' && !fieldDef.asMenu">
            <VTextField v-model="date" v-bind="fieldDef"
                        @click:prepend-inner.left="dialog = !dialog"
                        @click:append.left="onAppendIcon"
                        @keydown.down="onDateDown"
                        @keydown.up="onDateUp"
            />
            <VDialog ref="dialog" v-model="dialog" :return-value.sync="date" persistent width="290">
                <VDatePicker v-model="date" v-bind="datePickerDef" scrollable>
                    <VSpacer/>
                    <VBtn text @click.left="dialog = false">{{ $vuetify.lang.t('$vuetify.actions.cancel') }}</VBtn>
                    <VBtn text @click.left="$refs.dialog.save(date)">{{ $vuetify.lang.t('$vuetify.actions.ok') }}</VBtn>
                </VDatePicker>
            </VDialog>
        </template>
        <VDateRange v-else-if="field.type === 'daterange'" v-bind="fieldDef" @change="onChange"/>
        <template v-else-if="field.type === 'time' && fieldDef.asMenu">
            <VMenu
                ref="dialog"
                v-model="dialog"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                max-width="290"
            >
                <template v-slot:activator="{ on }">
                    <VTextField v-model="time" v-bind="fieldDef" v-on="on"/>
                </template>
                <VTimePicker v-model="time" v-bind="timePickerDef" scrollable>
                    <VSpacer/>
                    <VBtn text @click.left="dialog = false">{{ $vuetify.lang.t('$vuetify.actions.cancel') }}</VBtn>
                    <VBtn text color="primary" @click.left="$refs.dialog.save(time)">{{ $vuetify.lang.t('$vuetify.actions.ok') }}</VBtn>
                </VTimePicker>
            </VMenu>
        </template>
        <template v-else-if="field.type === 'time' && !fieldDef.asMenu">
            <VTextField v-model="time" v-bind="fieldDef"
                        @click:prepend-inner.left="dialog = !dialog"
                        @keydown.down="onTimeDown"
                        @keydown.up="onTimeUp"
            />
            <VDialog ref="dialog" v-model="dialog" :return-value.sync="time" persistent width="290">
                <VTimePicker v-if="dialog" v-model="time" v-bind="timePickerDef" scrollable>
                    <VSpacer/>
                    <VBtn text @click.left="dialog = false">{{ $vuetify.lang.t('$vuetify.actions.cancel') }}</VBtn>
                    <VBtn text @click.left="$refs.dialog.save(time)">{{ $vuetify.lang.t('$vuetify.actions.ok') }}</VBtn>
                </VTimePicker>
            </VDialog>
        </template>
        <VCheckbox v-else-if="field.type === 'checkbox'" v-bind="fieldDef" @change="onChange"/>
        <VSwitch v-else-if="field.type === 'switch'" v-bind="fieldDef" @change="onChange"/>
        <VCombobox v-else-if="field.type === 'combobox'" v-bind="fieldDef" :search-input.sync="search" @change="onChange">
            <template v-if="fieldDef.chips" slot="selection" slot-scope="data">
                <VChip
                    :key="JSON.stringify(data.item)"
                    :input-value="data.selected"
                    :disabled="data.disabled"
                    class="v-chip--select-multi"
                    pill
                    close
                    @input="data.parent.selectItem(data.item)"
                    @click.native="onClickChip(data.item)"
                    @click:close="onCloseChip(data.item)"
                >
                    <VAvatar v-if="data.item.avatar" class="accent" left>
                        <VIcon v-if="typeof data.item.avatar === 'string' && data.item.avatar.startsWith('mdi')">{{ data.item.avatar }}</VIcon>
                        <span v-else class="font-weight-bold">{{ data.item.avatar }}</span>
                    </VAvatar>
                    {{ data.item.label }}
                </VChip>
            </template>
            <template v-if="search" v-slot:no-data>
                <VListItem>
                    <VListItemContent>
                        <VListItemTitle>
                            Keine Treffer für "<strong>{{ search }}</strong>". Mit <kbd>RETURN</kbd> bestätigen für Neuanlage.
                        </VListItemTitle>
                    </VListItemContent>
                </VListItem>
            </template>
        </VCombobox>
        <VSelect v-else-if="field.type === 'select'" v-bind="fieldDef" @change="onChange">
            <template v-slot:item="{item}">
                <VListItemContent>
                    <VListItemTitle>{{ item.text }}</VListItemTitle>
                </VListItemContent>
                <VListItemAction v-if="item.action">
                    <VIcon>{{ item.action }}</VIcon>
                </VListItemAction>
            </template>
            <template v-if="field.prependItemText" v-slot:prepend-item>
                <VListItem dense disabled>
                    <VListItemContent>
                        <VListItemTitle>{{ field.prependItemText }}</VListItemTitle>
                    </VListItemContent>
                    <VListItemAvatar v-if="field.prependItemAction" size="24">
                        <VIcon>{{ field.prependItemAction }}</VIcon>
                    </VListItemAvatar>
                </VListItem>
                <VDivider class="mb-2"/>
            </template>
        </VSelect>
        <VRadioGroup v-else-if="field.type === 'radio'" v-bind="fieldDef" @change="onChange">
            <VRadio
                v-for="n in fieldDef.items"
                :key="n.value"
                :label="`${n.label}`"
                :value="n.value"
            />
        </VRadioGroup>
        <VSlider v-else-if="field.type === 'slider'" v-bind="fieldDef" @change="onChange">
            <template v-if="field.showInput" v-slot:append>
                <VTextField
                    :value="fieldDef.value"
                    type="number"
                    class="mt-0 ml-5 pt-0"
                    single-line
                    hide-details
                    :suffix="fieldDef.suffix"
                    :min="fieldDef.min"
                    :max="fieldDef.max"
                    :step="fieldDef.step"
                    :tabindex="fieldDef.tabindex"
                    :rules="[
                        v => (v >= fieldDef.min && v <= fieldDef.max) || 'Wert ist nicht im gültigen Bereich!'
                    ]"
                    style="width:64px"
                    @input="onInput"
                />
            </template>
        </VSlider>
        <VGridformSlider v-else-if="field.type === 'slider'" v-bind="fieldDef" @change="onChange"/>
        <VRangeSlider v-else-if="field.type === 'range'" v-bind="fieldDef" @change="onChange"/>
        <VRating v-else-if="field.type === 'rating'" :value="field.value" :readonly="fieldDef.disabled" @input="onInput"/>
        <VSpacer v-else-if="field.type === 'spacer'"/>
        <VSheet v-else-if="field.type === 'sparkline'" color="transparent">
            <VSparkline
                :value="field.value"
                :labels="field.labels"
                :padding="16"
                :line-width="2"
                auto-draw
            />
        </VSheet>
        <blockquote v-else-if="field.type === 'infotext'" class="blockquote">{{ field.value }}</blockquote>
        <VBtn v-else-if="field.type === 'button'" v-bind="fieldDef" @click="onClick">
            {{ field.label }}
            <VIcon v-if="fieldDef.appendIcon" right small v-text="fieldDef.appendIcon"/>
        </VBtn>
        <VSubheader v-else-if="field.type === 'subheader'" v-bind="fieldDef">{{ field.label }}</VSubheader>
        <template v-else-if="field.type === 'divider'">
            <VSubheader v-if="field.label" :class="fieldDef.class">
                <VBtn v-if="field.toggle" icon small @click.left="onToggle">
                    <VIcon small v-text="foldStateIcon(field.collapsed)"/>
                </VBtn>
                <div v-if="field.label" class="vgf-f__divider-label ml-3">{{ field.label }}</div>
                <VChip v-if="field.errorCount && field.collapsed" label small color="error" class="mr-2 font-weight-bold">{{ field.errorCount }}</VChip>
                <div v-if="field.hint && field.collapsed">{{ field.hint }}</div>
            </VSubheader>
            <VDivider/>
        </template>
        <VDivider v-else-if="field.type === 'gap'" style="margin:24px 10px"/>
        <VRatingButton v-else-if="field.type === 'ratingbutton'" v-bind="fieldDef"/>
        <PasswordInput v-else-if="field.type === 'password' && field.experimental"></PasswordInput>

        <!--isText at last because isText matches all text input fields-->
        <VTextField v-else-if="isText" v-bind="fieldDef" @input="onInput" @change="onChange" @click:append="onAppendIcon" @click:prepend-inner="onPrependInnerIcon" @keydown.enter="onSubmit"/>
        <VAlert v-else :value="true" type="error">Not supported! field.type={{ field.type }}</VAlert>

        <slot name="rating" :field="fieldDef" :cfg="config" :rowinfo="rowinfo" :data="fieldData"/>

        <!--Hover Menu-->
        <VExpandTransition>
            <VContainer
                v-if="field.hovered"
                class="vgf-f__hovered transition-fast-in-fast-out"
                pa-0
            >
                <VLayout class="fill-height">
                    <VFlex>
                        <VLayout column class="align-end justify-space-between fill-height">
                            <VSheet color="transparent">
                                <template v-for="btn in hoverButtonsGlobal">
                                    <VTooltip :key="btn.icon" top>
                                        <VBtn
                                            slot="activator"
                                            class="vgf-f__hover-btn"
                                            icon
                                            small
                                            flat
                                            dark
                                            @click="onHoverChange"
                                        >
                                            <VIcon small>{{ btn.icon }}</VIcon>
                                        </VBtn>
                                        <span>{{ btn.hint }}</span>
                                    </VTooltip>
                                </template>
                            </VSheet>
                            <VSheet color="transparent">
                                <template v-for="btn in hoverButtons">
                                    <VTooltip :key="btn.icon" bottom>
                                        <VBtn
                                            slot="activator"
                                            class="vgf-f__hover-btn"
                                            icon
                                            small
                                            flat
                                            dark
                                            @click="onHoverChange"
                                        >
                                            <VIcon small>{{ btn.icon }}</VIcon>
                                        </VBtn>
                                        <span>{{ btn.hint }}</span>
                                    </VTooltip>
                                </template>
                            </VSheet>
                        </VLayout>
                    </VFlex>
                </VLayout>
            </VContainer>
        </VExpandTransition>

        <slot name="append" :field="fieldDef" :cfg="config" :rowinfo="rowinfo" :data="fieldData"/>
    </VFlex>
</template>

<style lang="scss" scoped>
    .vgf-f__wrapper {
        position: relative;
        pre {
            font-size: 8px;
        }
        blockquote {
            padding-left: 32px;
        }
        .vgf-f__hover-btn {
            margin: 0;
        }
        .v-btn.vgf-f-button {
            margin-top: 2px;
        }
        .v-input--selection-controls {
            margin-top: 0;
        }
        .vgf-f__divider-label {
            width: calc(50% - 20px);
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
    }
    .vgf-f__hovered {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        background-color: rgba(0, 0, 0, 0.5);
    }
</style>

<!-- non scoped styles to fix some vuetify issues -->
<style lang="scss">
    // increase gap between tick labels and hint
    .vgf-f-slider,
    .vgf-f-range {
        .v-input__slot {
            margin-bottom: 16px !important;
        }
        .v-label {
            min-width: 320px;
            max-width: 320px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
        .v-slider__tick {
            cursor: pointer;
        }
    }

    .v-subheader.v-subheader--dense {
        height: 40px;
        padding: 0 8px;
    }

    .v-slider__tick-label {
        // font-size: 10px;
    }
    .v-application--is-ltr .v-slider--horizontal .v-slider__tick:last-child .v-slider__tick-label {
        transform: translateX(-50%);
    }

    .vgf-f-password input:-webkit-autofill,
    .vgf-f-text input:-webkit-autofill {
        // Make the backgound color become yellow _really slowly_
        transition: background-color 50000s ease-in-out 0s;

        // Expose a hook for JavaScript when auto fill is shown.
        // JavaScript can capture 'animationstart' events
        animation-name: onAutoFillStart;
    }

    .vgf-f-password input:not(:-webkit-autofill),
    .vgf-f-text input:not(:-webkit-autofill) {
        // Expose a hook for JS onAutoFillCancel
        // JavaScript can capture 'animationstart' events
        animation-name: onAutoFillCancel;
    }

    @keyframes onAutoFillStart {
        from {/* empty */}
        to {/* empty */}
    }

    @keyframes onAutoFillCancel {
        from {/* empty */}
        to {/* empty */}
    }
</style>
